import { type FormEvent, type FunctionComponent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "@reduxjs/toolkit";
import { actionCreators, type State } from "@App/state";
import { useTranslation } from "react-i18next";
import { TokenError } from "@App/apis/rest/ayvens/token/tokenError";
import type { DriverDataApi } from "@App/apis/rest/ayvens/driverData/interfaces";
import type { TokenApi } from "@App/apis/rest/ayvens/token/interfaces";
import { useVehicleSelector } from "@App/Pages/Partner/Ayvens/BusinessLogic/backToVehicleSelector";
import { AgentNumberPlateInputForm } from "@App/Components/organisms/whitelabel/ayvens/AgentNumberPlateInputForm";
import { AgentDriverDataVerificationForm } from "@App/Components/organisms/whitelabel/ayvens/AgentDriverDataVerificationForm";
import { DriverDataSuccessResponse } from "@App/apis/rest/ayvens/driverData/responses/driverDataSuccessResponse";

export type AgentVehicleSelectorProps = {
  driverDataApi: DriverDataApi;
  tokenApi: TokenApi;
  onSubmit: (token: string) => void;
};

export const AgentVehicleSelector: FunctionComponent<AgentVehicleSelectorProps> = ({
  driverDataApi,
  tokenApi,
  onSubmit,
}: AgentVehicleSelectorProps) => {
  const dispatch = useDispatch();
  const { setNumberPlate } = bindActionCreators(actionCreators, dispatch);
  const userId = useSelector((state: State) => state.auth.userId);
  const numberPlate = useSelector((state: State) => state.orders.numberPlate);
  const [numberPlateError, setNumberPlateError] = useState<string | null>(null);
  const [token, setToken] = useState<string | null>(null);

  const [driverData, setDriverData] = useState<DriverDataSuccessResponse | null>(null);

  const { resetTokenAndDriverData } = useVehicleSelector();

  const { t } = useTranslation();

  // on browser-back, the token and driver data should be reset
  useEffect(() => {
    resetTokenAndDriverData();
  }, []);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    resetTokenAndDriverData(null, true); // Reset token and driver data, but keep the number plate

    try {
      if (!userId || !numberPlate) {
        setNumberPlateError(t("vehicleSelector.validation.errors.error.invalid_request")); // Etwas ist schiefgelaufen
        return;
      }

      const token = await tokenApi.createToken(userId, numberPlate);
      setToken(token);

      const driverData = await driverDataApi.getFromJwt(token);
      setDriverData(driverData);
    } catch (error) {
      if (error instanceof TokenError) {
        // Display a user-friendly error message based on error.response.name
        setNumberPlateError(t(`vehicleSelector.validation.errors.error.${error.response.name}`));
      } else {
        // Display a general error message
        setNumberPlateError(t("vehicleSelector.validation.errors.error.invalid_request")); // Etwas ist schiefgelaufen
      }
    }
  };

  return (
    <div className={"min-h-screen"}>
      <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <h2 className="mt-10 text-center text-2xl/9 font-bold tracking-tight text-gray-900 font-serif">
            {t("vehicleSelector.title")}
          </h2>
        </div>

        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          {!driverData && (
            <AgentNumberPlateInputForm
              numberPlate={numberPlate}
              setNumberPlate={setNumberPlate}
              onSubmit={handleSubmit}
              error={numberPlateError}
            />
          )}
          {driverData && (
            <AgentDriverDataVerificationForm
              driverData={driverData}
              onCancel={() => setDriverData(null)}
              onSubmit={() => onSubmit(token)}
            />
          )}
        </div>
      </div>
    </div>
  );
};
